import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ReactMarkdown, { type Components } from 'react-markdown'
import { useTranslation } from 'react-i18next'
import { ChevronRight, Code2, Eye, File as FileIcon, FilePlus2, Folder, FolderUp, ImagePlus, Loader2, Plus, Trash2 } from 'lucide-react'
import {
  createOwnedDirectorSkillVersion,
  readDirectorSkillFile,
  readDirectorSkillPackageLimits,
  readDirectorSkillStructuredError,
  skillFileToUpload,
  type DirectorSkillSummary,
  validateDirectorSkillDocument,
} from '../../api/directorSkills'
import type { SkillFileView, SkillPackageLimits, SkillPackageOperation, SkillVersionResult, SkillView } from '../../generated/directorSkillTypes'
import { createNadDebug } from '../../utils/debugLog'
import { isTextSkillFile, readSkillDocumentFields, skillDocumentBody, type SkillDocumentFields, updateSkillDocumentFields } from './skillDocument'
import { SkillCoverMedia } from './SkillCoverMedia'
import {
  calculateSkillPackageUsage,
  canonicalizeSkillReferencePath,
  findSkillPackageLimitViolation,
  hasSkillPathCollision,
  prepareSkillReference,
  readStrictUtf8,
  SkillReferenceInputError,
} from './skillPackage'
import { trackSkillLibraryAction } from '../../analytics/chatHeaderAnalytics'
import { useOptionalProjectId } from '../../contexts/ProjectIdContext'

const editorLog = createNadDebug('dw:director-skills:editor')
const SKILL_DOCUMENT_PATH = 'SKILL.md'

const skillMarkdownComponents: Components = {
  h1: ({ children }) => <h1 className="mb-5 text-2xl font-semibold tracking-tight text-foreground">{children}</h1>,
  h2: ({ children }) => <h2 className="mb-3 mt-8 text-xl font-semibold tracking-tight text-foreground first:mt-0">{children}</h2>,
  h3: ({ children }) => <h3 className="mb-2 mt-6 text-lg font-semibold text-foreground">{children}</h3>,
  h4: ({ children }) => <h4 className="mb-2 mt-5 text-base font-semibold text-foreground">{children}</h4>,
  p: ({ children }) => <p className="my-3 text-base leading-7 text-muted-foreground">{children}</p>,
  strong: ({ children }) => <strong className="font-semibold text-foreground">{children}</strong>,
  em: ({ children }) => <em className="text-foreground/90">{children}</em>,
  ul: ({ children }) => <ul className="my-4 list-disc space-y-2 pl-6 text-base leading-7 text-muted-foreground marker:text-text-muted">{children}</ul>,
  ol: ({ children }) => <ol className="my-4 list-decimal space-y-2 pl-6 text-base leading-7 text-muted-foreground marker:text-text-muted">{children}</ol>,
  li: ({ children }) => <li className="pl-1">{children}</li>,
  blockquote: ({ children }) => (
    <blockquote className="my-5 border-l-2 border-primary/60 bg-surface-1 px-4 py-1 text-muted-foreground">{children}</blockquote>
  ),
  code: ({ children }) => <code className="rounded bg-surface-2 px-1.5 py-0.5 font-mono text-sm text-foreground">{children}</code>,
  pre: ({ children }) => (
    <pre className="my-5 overflow-x-auto rounded-lg border border-border bg-background p-4 font-mono text-sm leading-relaxed text-muted-foreground [&>code]:bg-transparent [&>code]:p-0 [&>code]:text-inherit">
      {children}
    </pre>
  ),
  hr: () => <hr className="my-8 border-border-subtle" />,
  a: ({ children, href }) => (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="font-medium text-primary underline decoration-primary/40 underline-offset-4 hover:decoration-primary"
    >
      {children}
    </a>
  ),
  table: ({ children }) => <table className="my-5 w-full border-collapse overflow-hidden text-left text-sm">{children}</table>,
  thead: ({ children }) => <thead className="bg-surface-2 text-foreground">{children}</thead>,
  th: ({ children }) => <th className="border border-border px-3 py-2 font-semibold">{children}</th>,
  td: ({ children }) => <td className="border border-border px-3 py-2 text-muted-foreground">{children}</td>,
}

interface SkillEditorProps {
  skill: DirectorSkillSummary
  detail: SkillView
  onCancel: () => void
  onSaved: (result: SkillVersionResult) => void
}

interface PendingFile {
  file: File
  path: string
}

function visiblePackageFiles(files: SkillFileView[] | undefined): SkillFileView[] {
  return (files ?? []).filter((file) => file.path !== 'SKILL.md' && file.role !== 'cover_preview')
}

function displayFileName(path: string): string {
  const parts = path.split('/')
  return parts[parts.length - 1] || path
}

interface PackageTreeNode {
  folders: Map<string, PackageTreeNode>
  files: string[]
}

interface PackageTreeEntry {
  kind: 'folder' | 'file'
  path: string
  depth: number
}

function packageTreeEntries(paths: string[]): PackageTreeEntry[] {
  const root: PackageTreeNode = { folders: new Map(), files: [] }
  for (const path of paths) {
    const parts = path.split('/')
    const leaf = parts.pop()
    if (!leaf) continue
    let node = root
    for (const folder of parts) {
      let child = node.folders.get(folder)
      if (!child) {
        child = { folders: new Map(), files: [] }
        node.folders.set(folder, child)
      }
      node = child
    }
    node.files.push(path)
  }

  const entries: PackageTreeEntry[] = []
  const walk = (node: PackageTreeNode, parentPath: string, depth: number) => {
    for (const [name, child] of [...node.folders].sort(([left], [right]) => left.localeCompare(right))) {
      const path = parentPath ? `${parentPath}/${name}` : name
      entries.push({ kind: 'folder', path, depth })
      walk(child, path, depth + 1)
    }
    for (const path of [...node.files].sort((left, right) => left.localeCompare(right))) {
      entries.push({ kind: 'file', path, depth })
    }
  }
  walk(root, '', 0)
  return entries
}

function localCoverKind(file: File | undefined): 'image' | 'video' | null {
  if (!file) return null
  if (file.type.startsWith('image/')) return 'image'
  if (file.type.startsWith('video/')) return 'video'
  return null
}

export function SkillEditor({ skill, detail, onCancel, onSaved }: SkillEditorProps) {
  const { t } = useTranslation('chat')
  const fallbackFields = useMemo<SkillDocumentFields>(
    () => ({
      name: detail.name ?? skill.name,
      displayName: detail.display_name ?? skill.displayName,
      description: detail.description ?? skill.description,
      useWhen: detail.use_when ?? skill.useWhen,
      howToUse: detail.how_to_use ?? skill.howToUse,
      outputContent: detail.output_content ?? skill.outputContent,
    }),
    [detail, skill],
  )
  const initialContent = detail.content_md ?? updateSkillDocumentFields('', fallbackFields)
  const [content, setContent] = useState(initialContent)
  const [fields, setFields] = useState(() => readSkillDocumentFields(initialContent, fallbackFields))
  const [selectedPath, setSelectedPath] = useState(SKILL_DOCUMENT_PATH)
  const [previewMode, setPreviewMode] = useState<'preview' | 'code'>('preview')
  const [pendingFiles, setPendingFiles] = useState<Map<string, PendingFile>>(new Map())
  const [removedPaths, setRemovedPaths] = useState<Set<string>>(new Set())
  const [renamedPaths, setRenamedPaths] = useState<Map<string, string>>(new Map())
  const [loadedText, setLoadedText] = useState<Map<string, string>>(new Map())
  const [coverPath, setCoverPath] = useState(detail.cover?.file_path ?? null)
  const [saving, setSaving] = useState(false)
  const [loadingFile, setLoadingFile] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fileError, setFileError] = useState<string | null>(null)
  const [documentError, setDocumentError] = useState<string | null>(null)
  const [validatedContent, setValidatedContent] = useState<string | null>(null)
  const [validationError, setValidationError] = useState<string | null>(null)
  const [validationLoading, setValidationLoading] = useState(true)
  const [validationAttempt, setValidationAttempt] = useState(0)
  const [limits, setLimits] = useState<SkillPackageLimits | null>(null)
  const [limitsError, setLimitsError] = useState<string | null>(null)
  const [limitsLoading, setLimitsLoading] = useState(true)
  const [newFileOpen, setNewFileOpen] = useState(false)
  const [newFilePath, setNewFilePath] = useState('')
  const [renameOpen, setRenameOpen] = useState(false)
  const [renamePath, setRenamePath] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)
  const folderInputRef = useRef<HTMLInputElement>(null)
  const coverInputRef = useRef<HTMLInputElement>(null)
  const loggedViolationRef = useRef<string | null>(null)

  const existingFiles = useMemo(() => visiblePackageFiles(detail.files), [detail.files])
  const paths = useMemo(() => {
    const next = new Set(existingFiles.map((file) => file.path))
    for (const path of removedPaths) next.delete(path)
    for (const [source, target] of renamedPaths) {
      next.delete(source)
      next.add(target)
    }
    for (const path of pendingFiles.keys()) next.add(path)
    return [...next].sort((left, right) => left.localeCompare(right))
  }, [existingFiles, pendingFiles, removedPaths, renamedPaths])
  const treeEntries = useMemo(() => packageTreeEntries(paths), [paths])
  const packageDirty = pendingFiles.size > 0 || removedPaths.size > 0 || renamedPaths.size > 0 || coverPath !== (detail.cover?.file_path ?? null)
  const selectedSourcePath = [...renamedPaths].find(([, target]) => target === selectedPath)?.[0] ?? selectedPath
  const selectedExisting = existingFiles.find((file) => file.path === selectedSourcePath)
  const selectedPending = pendingFiles.get(selectedPath)
  const selectedMediaType = selectedPending?.file.type || selectedExisting?.media_type || ''
  const selectedIsText = selectedPath === SKILL_DOCUMENT_PATH || isTextSkillFile(selectedMediaType, selectedPath)
  const selectedIsReference = selectedPath !== SKILL_DOCUMENT_PATH && selectedPath !== coverPath && selectedIsText
  const selectedText = selectedPath === SKILL_DOCUMENT_PATH ? content : (loadedText.get(selectedPath) ?? '')
  const documentBody = useMemo(() => {
    try {
      return skillDocumentBody(content)
    } catch {
      return content
    }
  }, [content])
  const pendingCover = coverPath ? pendingFiles.get(coverPath)?.file : undefined
  const pendingCoverKind = localCoverKind(pendingCover)
  const [pendingCoverUrl, setPendingCoverUrl] = useState<string | null>(null)
  const packageUsage = useMemo(
    () =>
      calculateSkillPackageUsage(
        content,
        paths.map((path) => ({
          path,
          role: path === coverPath ? 'cover' : 'reference',
          sizeBytes:
            pendingFiles.get(path)?.file.size ??
            existingFiles.find((file) => file.path === ([...renamedPaths].find(([, target]) => target === path)?.[0] ?? path))?.size_bytes ??
            0,
        })),
      ),
    [content, coverPath, existingFiles, paths, pendingFiles, renamedPaths],
  )
  const limitViolation = useMemo(() => (limits ? findSkillPackageLimitViolation(packageUsage, limits) : null), [limits, packageUsage])
  const metadataFields: Array<{
    field: keyof Omit<SkillDocumentFields, 'name' | 'displayName' | 'description'>
    label: string
  }> = [
    { field: 'useWhen', label: t('skillPicker.usageScenario') },
    { field: 'howToUse', label: t('skillPicker.howToUse') },
    { field: 'outputContent', label: t('skillPicker.outputContent') },
  ]

  useEffect(() => {
    if (!pendingCover) {
      setPendingCoverUrl(null)
      return
    }
    const next = URL.createObjectURL(pendingCover)
    setPendingCoverUrl(next)
    return () => URL.revokeObjectURL(next)
  }, [pendingCover])

  useEffect(() => {
    folderInputRef.current?.setAttribute('webkitdirectory', '')
    folderInputRef.current?.setAttribute('directory', '')
  }, [])

  const loadLimits = useCallback(async () => {
    setLimitsLoading(true)
    setLimitsError(null)
    try {
      const next = await readDirectorSkillPackageLimits()
      setLimits(next)
      editorLog({
        event: 'skill-editor:limits-loaded',
        trace_id: `director-skills-editor:${skill.skillId}`,
        skill_id: skill.skillId,
        max_user_file_count: next.max_user_file_count,
        max_user_payload_bytes: next.max_user_payload_bytes,
        outcome: 'success',
      })
    } catch (reason) {
      setLimits(null)
      const message = reason instanceof Error ? reason.message : String(reason)
      setLimitsError(message)
      editorLog({
        event: 'skill-editor:limits-load-failed',
        trace_id: `director-skills-editor:${skill.skillId}`,
        skill_id: skill.skillId,
        error: message,
        outcome: 'error',
      })
    } finally {
      setLimitsLoading(false)
    }
  }, [skill.skillId])

  useEffect(() => {
    void loadLimits()
  }, [loadLimits])

  useEffect(() => {
    let active = true
    setValidatedContent(null)
    setValidationError(null)
    if (documentError) {
      setValidationLoading(false)
      return
    }
    setValidationLoading(true)
    const timer = window.setTimeout(() => {
      void validateDirectorSkillDocument({ content_md: content })
        .then((result) => {
          if (!active) return
          if (result.valid !== true) throw new Error('Director skill validation returned an invalid result.')
          setValidatedContent(content)
          setValidationLoading(false)
          editorLog({
            event: 'skill-editor:document-validated',
            trace_id: `director-skills-editor:${skill.skillId}`,
            skill_id: skill.skillId,
            outcome: 'success',
          })
        })
        .catch((reason) => {
          if (!active) return
          const structured = reason instanceof Error ? readDirectorSkillStructuredError(reason) : null
          const message = structured
            ? t('skillPicker.skillDocumentValidationFailed', {
                error: structured.code,
                field: structured.field ?? 'content_md',
              })
            : t('skillPicker.skillDocumentValidationFailed', {
                error: reason instanceof Error ? reason.message : String(reason),
                field: 'content_md',
              })
          setValidationError(message)
          setValidationLoading(false)
          editorLog({
            event: 'skill-editor:document-validation-failed',
            trace_id: `director-skills-editor:${skill.skillId}`,
            skill_id: skill.skillId,
            error: structured?.code ?? (reason instanceof Error ? reason.message : String(reason)),
            outcome: 'blocked',
          })
        })
    }, 250)
    return () => {
      active = false
      window.clearTimeout(timer)
    }
  }, [content, documentError, skill.skillId, t, validationAttempt])

  useEffect(() => {
    if (!limitViolation) {
      loggedViolationRef.current = null
      return
    }
    const key = `${limitViolation.kind}:${limitViolation.limit}:${limitViolation.actual}`
    if (loggedViolationRef.current === key) return
    loggedViolationRef.current = key
    editorLog({
      event: 'skill-editor:package-limit-exceeded',
      trace_id: `director-skills-editor:${skill.skillId}`,
      skill_id: skill.skillId,
      limit_kind: limitViolation.kind,
      limit: limitViolation.limit,
      actual: limitViolation.actual,
      outcome: 'blocked',
    })
  }, [limitViolation, skill.skillId])

  const referenceErrorMessage = useCallback(
    (reason: SkillReferenceInputError): string => {
      switch (reason.code) {
        case 'not-markdown':
          return t('skillPicker.referenceMarkdownOnly', { path: reason.path })
        case 'not-utf8':
          return t('skillPicker.referenceUtf8Only', { path: reason.path })
        case 'too-large':
          return t('skillPicker.referenceTooLarge', {
            path: reason.path,
            actual: reason.actual,
            limit: reason.limit,
          })
        case 'collision':
          return t('skillPicker.referenceCollision', { path: reason.path })
        case 'reserved-path':
          return t('skillPicker.referenceReservedPath', { path: reason.path })
        case 'invalid-path':
          return t('skillPicker.referenceInvalidPath', { path: reason.path })
      }
    },
    [t],
  )

  const updateField = useCallback(
    (field: keyof Omit<SkillDocumentFields, 'name'>, value: string) => {
      const next = { ...fields, [field]: value }
      try {
        const nextContent = updateSkillDocumentFields(content, next)
        setFields(next)
        setContent(nextContent)
        setDocumentError(null)
      } catch (reason) {
        setDocumentError(reason instanceof Error ? reason.message : String(reason))
      }
    },
    [content, fields],
  )

  const selectPath = useCallback(
    async (path: string) => {
      setSelectedPath(path)
      setRenameOpen(false)
      setRenamePath('')
      setError(null)
      if (path === SKILL_DOCUMENT_PATH || loadedText.has(path) || pendingFiles.has(path)) return
      const sourcePath = [...renamedPaths].find(([, target]) => target === path)?.[0] ?? path
      const existing = existingFiles.find((file) => file.path === sourcePath)
      if (!existing || !isTextSkillFile(existing.media_type, path)) return
      setLoadingFile(true)
      try {
        const blob = await readDirectorSkillFile(existing)
        const text = await blob.text()
        setLoadedText((current) => new Map(current).set(path, text))
      } catch (reason) {
        setError(reason instanceof Error ? reason.message : String(reason))
      } finally {
        setLoadingFile(false)
      }
    },
    [existingFiles, loadedText, pendingFiles, renamedPaths],
  )

  const addFiles = useCallback(
    async (files: File[]) => {
      if (!limits) {
        setFileError(t('skillPicker.packageLimitsRequired'))
        return
      }
      const additions = new Map<string, PendingFile>()
      const textAdditions = new Map<string, string>()
      const occupiedPaths = [...paths]
      let nextDocument: string | null = null
      let nextDocumentFields: SkillDocumentFields | null = null
      try {
        for (const file of files) {
          const relativePath = file.webkitRelativePath || file.name
          if (relativePath === limits.document_filename) {
            if (file.size > limits.max_document_bytes) {
              throw new SkillReferenceInputError('too-large', relativePath, limits.max_document_bytes, file.size)
            }
            nextDocument = await readStrictUtf8(file)
            continue
          }
          const prepared = await prepareSkillReference(file, relativePath, limits)
          if (hasSkillPathCollision([...occupiedPaths, ...additions.keys()], prepared.path)) {
            throw new SkillReferenceInputError('collision', prepared.path)
          }
          additions.set(prepared.path, {
            file: prepared.file,
            path: prepared.path,
          })
          textAdditions.set(prepared.path, prepared.text)
        }
        if (nextDocument !== null) nextDocumentFields = readSkillDocumentFields(nextDocument, fields)
      } catch (reason) {
        setFileError(reason instanceof SkillReferenceInputError ? referenceErrorMessage(reason) : reason instanceof Error ? reason.message : String(reason))
        return
      }
      setFileError(null)
      if (nextDocument !== null && nextDocumentFields !== null) {
        setContent(nextDocument)
        setFields(nextDocumentFields)
        setDocumentError(null)
        setSelectedPath(SKILL_DOCUMENT_PATH)
      }
      if (additions.size) {
        setPendingFiles((current) => new Map([...current, ...additions]))
        setRemovedPaths((current) => {
          const next = new Set(current)
          for (const path of additions.keys()) next.delete(path)
          return next
        })
        setLoadedText((current) => new Map([...current, ...textAdditions]))
        const addedPaths = [...additions.keys()]
        setSelectedPath(addedPaths[addedPaths.length - 1] ?? SKILL_DOCUMENT_PATH)
      }
    },
    [fields, limits, paths, referenceErrorMessage, t],
  )

  const addNewFile = useCallback(() => {
    if (!limits) {
      setFileError(t('skillPicker.packageLimitsRequired'))
      return
    }
    try {
      const path = canonicalizeSkillReferencePath(newFilePath, limits)
      if (hasSkillPathCollision(paths, path)) throw new SkillReferenceInputError('collision', path)
      const text = '\n'
      const file = new File([text], displayFileName(path), {
        type: limits.reference_media_type,
      })
      setPendingFiles((current) => new Map(current).set(path, { file, path }))
      setLoadedText((current) => new Map(current).set(path, text))
      setRemovedPaths((current) => {
        const next = new Set(current)
        next.delete(path)
        return next
      })
      setSelectedPath(path)
      setNewFilePath('')
      setNewFileOpen(false)
      setFileError(null)
    } catch (reason) {
      setFileError(reason instanceof SkillReferenceInputError ? referenceErrorMessage(reason) : reason instanceof Error ? reason.message : String(reason))
    }
  }, [limits, newFilePath, paths, referenceErrorMessage, t])

  const renameSelected = useCallback(() => {
    if (!limits || selectedPath === SKILL_DOCUMENT_PATH || selectedPath === coverPath) return
    try {
      const target = canonicalizeSkillReferencePath(renamePath, limits)
      if (target === selectedPath) {
        setRenameOpen(false)
        setRenamePath('')
        return
      }
      if (
        hasSkillPathCollision(
          paths.filter((path) => path !== selectedPath),
          target,
        )
      ) {
        throw new SkillReferenceInputError('collision', target)
      }
      const originalSource = [...renamedPaths].find(([, currentTarget]) => currentTarget === selectedPath)?.[0]
      const source = originalSource ?? selectedPath
      const existsInSavedPackage = existingFiles.some((file) => file.path === source && file.role === 'reference')

      setRenamedPaths((current) => {
        const next = new Map(current)
        if (originalSource) next.delete(originalSource)
        if (existsInSavedPackage) next.set(source, target)
        return next
      })
      setPendingFiles((current) => {
        const pending = current.get(selectedPath)
        if (!pending) return current
        const next = new Map(current)
        next.delete(selectedPath)
        next.set(target, {
          path: target,
          file: new File([pending.file], displayFileName(target), {
            type: limits.reference_media_type,
          }),
        })
        return next
      })
      setLoadedText((current) => {
        if (!current.has(selectedPath)) return current
        const next = new Map(current)
        const value = next.get(selectedPath) ?? ''
        next.delete(selectedPath)
        next.set(target, value)
        return next
      })
      setSelectedPath(target)
      setRenameOpen(false)
      setRenamePath('')
      setFileError(null)
    } catch (reason) {
      setFileError(reason instanceof SkillReferenceInputError ? referenceErrorMessage(reason) : reason instanceof Error ? reason.message : String(reason))
    }
  }, [coverPath, existingFiles, limits, paths, referenceErrorMessage, renamePath, renamedPaths, selectedPath])

  const replaceCover = useCallback(
    (file: File) => {
      if (!limits) {
        setFileError(t('skillPicker.packageLimitsRequired'))
        return
      }
      const kind = localCoverKind(file)
      if (!kind) {
        setFileError(t('skillPicker.coverTypeUnsupported'))
        return
      }
      const maxBytes = kind === 'image' ? limits.max_cover_image_bytes : limits.max_cover_video_bytes
      if (file.size > maxBytes) {
        setFileError(
          t('skillPicker.coverTooLarge', {
            actual: file.size,
            limit: maxBytes,
          }),
        )
        return
      }
      const nextPath = `cover/${file.name}`
      const previousPath = coverPath
      if (nextPath !== previousPath && hasSkillPathCollision(paths, nextPath)) {
        setFileError(t('skillPicker.referenceCollision', { path: nextPath }))
        return
      }
      setPendingFiles((current) => {
        const next = new Map(current)
        if (previousPath && previousPath !== nextPath) next.delete(previousPath)
        next.set(nextPath, { file, path: nextPath })
        return next
      })
      if (previousPath && previousPath !== nextPath && existingFiles.some((item) => item.path === previousPath)) {
        setRemovedPaths((current) => new Set(current).add(previousPath))
      }
      setCoverPath(nextPath)
      setFileError(null)
    },
    [coverPath, existingFiles, limits, paths, t],
  )

  const removeCover = useCallback(() => {
    if (coverPath) {
      setPendingFiles((current) => {
        const next = new Map(current)
        next.delete(coverPath)
        return next
      })
      if (existingFiles.some((file) => file.path === coverPath && file.role === 'cover')) {
        setRemovedPaths((current) => new Set(current).add(coverPath))
      }
    }
    setCoverPath(null)
  }, [coverPath, existingFiles])

  const removeSelected = useCallback(() => {
    if (selectedPath === SKILL_DOCUMENT_PATH) return
    if (selectedPath === coverPath) {
      removeCover()
      setSelectedPath(SKILL_DOCUMENT_PATH)
      return
    }
    const originalSource = [...renamedPaths].find(([, target]) => target === selectedPath)?.[0]
    setPendingFiles((current) => {
      const next = new Map(current)
      next.delete(selectedPath)
      return next
    })
    if (originalSource) {
      setRenamedPaths((current) => {
        const next = new Map(current)
        next.delete(originalSource)
        return next
      })
      setRemovedPaths((current) => new Set(current).add(originalSource))
    } else if (existingFiles.some((file) => file.path === selectedPath && file.role === 'reference')) {
      setRemovedPaths((current) => new Set(current).add(selectedPath))
    }
    setRenameOpen(false)
    setRenamePath('')
    setSelectedPath(SKILL_DOCUMENT_PATH)
  }, [coverPath, existingFiles, removeCover, renamedPaths, selectedPath])

  const updateSelectedText = useCallback(
    (value: string) => {
      if (selectedPath === SKILL_DOCUMENT_PATH) {
        setContent(value)
        try {
          setFields(readSkillDocumentFields(value, fields))
          setDocumentError(null)
        } catch (reason) {
          setDocumentError(reason instanceof Error ? reason.message : String(reason))
        }
        return
      }
      const file = new File([value], displayFileName(selectedPath), {
        type: limits?.reference_media_type ?? selectedMediaType,
      })
      setLoadedText((current) => new Map(current).set(selectedPath, value))
      setPendingFiles((current) => new Map(current).set(selectedPath, { file, path: selectedPath }))
    },
    [fields, limits?.reference_media_type, selectedMediaType, selectedPath],
  )

  const buildPackageOperations = useCallback(async (): Promise<SkillPackageOperation[]> => {
    const operations: SkillPackageOperation[] = []
    for (const path of [...removedPaths].sort((left, right) => left.localeCompare(right))) {
      if (existingFiles.some((file) => file.path === path && file.role === 'reference')) {
        operations.push({ op: 'delete_reference', path })
      }
    }
    for (const [path, newPath] of [...renamedPaths].sort(([left], [right]) => left.localeCompare(right))) {
      operations.push({ op: 'rename_reference', path, new_path: newPath })
    }
    for (const [path, pending] of [...pendingFiles].sort(([left], [right]) => left.localeCompare(right))) {
      if (path === coverPath) continue
      const upload = await skillFileToUpload(pending.file, path)
      operations.push({
        op: 'put_reference',
        file: {
          path: upload.path,
          content_base64: upload.content_base64,
        },
      })
    }
    const initialCoverPath = detail.cover?.file_path ?? null
    const pendingCoverFile = coverPath ? pendingFiles.get(coverPath)?.file : undefined
    if (pendingCoverFile && coverPath) {
      operations.push({
        op: 'set_cover',
        file: await skillFileToUpload(pendingCoverFile, coverPath),
      })
    } else if (initialCoverPath && coverPath === null) {
      operations.push({ op: 'clear_cover' })
    }
    return operations
  }, [coverPath, detail.cover?.file_path, existingFiles, pendingFiles, removedPaths, renamedPaths])

  const analyticsProjectId = useOptionalProjectId()
  const save = useCallback(async () => {
    if (!limits || limitsError || limitViolation || validationLoading || validationError || validatedContent !== content) return
    setSaving(true)
    setError(null)
    try {
      const operations = packageDirty ? await buildPackageOperations() : undefined
      const result = await createOwnedDirectorSkillVersion(skill.skillId, {
        content_md: content,
        expected_version: detail.version ?? skill.version,
        activate: true,
        ...(operations === undefined ? {} : { operations }),
      })
      editorLog({
        event: 'skill-editor:saved',
        trace_id: `director-skills-editor:${skill.skillId}`,
        skill_id: skill.skillId,
        previous_version: detail.version ?? skill.version,
        new_version: result.version,
        package_replaced: packageDirty,
        operation_count: operations?.length ?? 0,
        cover_kind: result.cover?.kind ?? 'none',
        outcome: 'success',
      })
      onSaved(result)
    } catch (reason) {
      let message = reason instanceof Error ? reason.message : String(reason)
      if (reason instanceof Error) {
        const structured = readDirectorSkillStructuredError(reason)
        if (structured?.limit !== undefined && structured.actual !== undefined) {
          message = t('skillPicker.serverPackageLimitError', {
            error: structured.code,
            field: structured.field ?? 'files',
            actual: structured.actual,
            limit: structured.limit,
          })
        } else if (structured) {
          message = t('skillPicker.serverPackageError', {
            error: structured.code,
            field: structured.field ?? 'files',
          })
        }
      }
      setError(message)
      editorLog({
        event: 'skill-editor:save-failed',
        trace_id: `director-skills-editor:${skill.skillId}`,
        skill_id: skill.skillId,
        package_replaced: packageDirty,
        outcome: 'error',
      })
    } finally {
      setSaving(false)
    }
  }, [
    buildPackageOperations,
    content,
    detail.version,
    limitViolation,
    limits,
    limitsError,
    onSaved,
    packageDirty,
    skill.skillId,
    skill.version,
    t,
    validatedContent,
    validationError,
    validationLoading,
  ])

  const limitViolationMessage = limitViolation
    ? limitViolation.kind === 'document'
      ? t('skillPicker.documentLimitExceeded', {
          actual: limitViolation.actual,
          limit: limitViolation.limit,
        })
      : limitViolation.kind === 'reference'
        ? t('skillPicker.referenceTooLarge', {
            path: limitViolation.path,
            actual: limitViolation.actual,
            limit: limitViolation.limit,
          })
        : limitViolation.kind === 'file-count'
          ? t('skillPicker.packageFileLimitExceeded', {
              actual: limitViolation.actual,
              limit: limitViolation.limit,
            })
          : t('skillPicker.packagePayloadLimitExceeded', {
              actual: limitViolation.actual,
              limit: limitViolation.limit,
            })
    : null
  const packageActionsDisabled = saving || limitsLoading || limits === null || documentError !== null

  return (
    <div className="space-y-5" data-testid="director-skill-editor">
      <div className="grid gap-4 lg:grid-cols-2">
        <label className="space-y-1.5 text-sm text-muted-foreground">
          <span>{t('skillPicker.skillName')}</span>
          <input
            value={fields.displayName}
            onChange={(event) => updateField('displayName', event.target.value)}
            className="h-10 w-full rounded-lg border border-input-border bg-input-bg px-3 text-base text-foreground outline-none placeholder:text-text-muted focus:border-primary focus:ring-2 focus:ring-primary/20"
            data-testid="skill-editor-name"
          />
        </label>
        <label className="space-y-1.5 text-sm text-muted-foreground">
          <span>{t('skillPicker.introduction')}</span>
          <input
            value={fields.description}
            onChange={(event) => updateField('description', event.target.value)}
            className="h-10 w-full rounded-lg border border-input-border bg-input-bg px-3 text-base text-foreground outline-none placeholder:text-text-muted focus:border-primary focus:ring-2 focus:ring-primary/20"
            data-testid="skill-editor-description"
          />
        </label>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        {metadataFields.map(({ field, label }) => (
          <label key={field} className="space-y-1.5 text-sm text-muted-foreground">
            <span>{label}</span>
            <textarea
              value={fields[field]}
              onChange={(event) => updateField(field, event.target.value)}
              rows={3}
              className="w-full resize-none rounded-lg border border-input-border bg-input-bg px-3 py-2 text-base leading-relaxed text-foreground outline-none placeholder:text-text-muted focus:border-primary focus:ring-2 focus:ring-primary/20"
              data-testid={`skill-editor-${field}`}
            />
          </label>
        ))}
      </div>

      <section aria-labelledby="skill-content-heading" className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 id="skill-content-heading" className="text-lg font-medium text-foreground">
              {t('skillPicker.skillContent')}
            </h3>
            <p className="mt-0.5 text-xs font-medium text-text-muted">{t('skillPicker.skillContentHint')}</p>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setPreviewMode('preview')}
              aria-pressed={previewMode === 'preview'}
              className={`inline-flex h-8 items-center gap-1.5 rounded-lg border px-2.5 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 ${previewMode === 'preview' ? 'border-input-border-hover bg-surface-2 text-foreground' : 'border-input-border bg-surface-1 text-muted-foreground hover:border-input-border-hover hover:bg-surface-3'}`}
              data-testid="skill-editor-preview-mode"
            >
              <Eye className="h-3.5 w-3.5" /> {t('skillPicker.preview')}
            </button>
            <button
              type="button"
              onClick={() => setPreviewMode('code')}
              aria-pressed={previewMode === 'code'}
              className={`inline-flex h-8 items-center gap-1.5 rounded-lg border px-2.5 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 ${previewMode === 'code' ? 'border-input-border-hover bg-surface-2 text-foreground' : 'border-input-border bg-surface-1 text-muted-foreground hover:border-input-border-hover hover:bg-surface-3'}`}
              data-testid="skill-editor-code-mode"
            >
              <Code2 className="h-3.5 w-3.5" /> {t('skillPicker.code')}
            </button>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={packageActionsDisabled}
              className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-input-border bg-surface-1 px-2.5 text-xs font-medium text-foreground hover:border-input-border-hover hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 disabled:cursor-not-allowed disabled:opacity-50"
              title={t('skillPicker.uploadFiles')}
              data-testid="skill-editor-upload-files"
            >
              <FilePlus2 className="h-3.5 w-3.5" /> {t('skillPicker.files')}
            </button>
            <button
              type="button"
              onClick={() => folderInputRef.current?.click()}
              disabled={packageActionsDisabled}
              className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-input-border bg-surface-1 px-2.5 text-xs font-medium text-foreground hover:border-input-border-hover hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 disabled:cursor-not-allowed disabled:opacity-50"
              title={t('skillPicker.uploadFolder')}
              data-testid="skill-editor-upload-folder"
            >
              <FolderUp className="h-3.5 w-3.5" /> {t('skillPicker.folder')}
            </button>
          </div>
        </div>

        <div className="grid h-[60vh] min-h-96 max-h-[680px] overflow-hidden rounded-xl border border-border bg-surface-0 lg:grid-cols-[230px_1fr]">
          <aside
            className="overflow-y-auto border-b border-border-subtle bg-background p-2 lg:border-b-0 lg:border-r"
            aria-label={t('skillPicker.skillContent')}
          >
            <div className="mb-1 flex items-center justify-between gap-2 px-2.5 py-1">
              <span className="text-xxs font-medium uppercase tracking-wide text-text-muted">{t('skillPicker.files')}</span>
              <button
                type="button"
                onClick={() => {
                  setNewFileOpen((current) => !current)
                  setFileError(null)
                }}
                disabled={!limits || limitsLoading}
                className="inline-flex h-7 items-center gap-1 rounded-lg border border-input-border bg-surface-1 px-2 text-xs font-medium text-foreground hover:border-input-border-hover hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 disabled:cursor-not-allowed disabled:opacity-50"
                data-testid="skill-editor-new-file"
              >
                <Plus className="h-3.5 w-3.5" /> {t('skillPicker.newFile')}
              </button>
            </div>
            {newFileOpen && (
              <form
                className="mb-2 space-y-2 rounded-lg border border-border bg-surface-1 p-2"
                onSubmit={(event) => {
                  event.preventDefault()
                  addNewFile()
                }}
              >
                <label className="block text-xs font-medium text-muted-foreground" htmlFor="skill-editor-new-file-path">
                  {t('skillPicker.filePath')}
                </label>
                <input
                  id="skill-editor-new-file-path"
                  value={newFilePath}
                  onChange={(event) => setNewFilePath(event.target.value)}
                  placeholder={t('skillPicker.filePathPlaceholder')}
                  className="h-8 w-full rounded-lg border border-input-border bg-input-bg px-2 text-xs font-medium text-foreground outline-none placeholder:text-text-muted focus:border-primary focus:ring-2 focus:ring-primary/20"
                  data-testid="skill-editor-new-file-path"
                  autoFocus
                />
                <div className="flex justify-end gap-1.5">
                  <button
                    type="button"
                    onClick={() => {
                      setNewFileOpen(false)
                      setNewFilePath('')
                    }}
                    className="h-7 rounded-lg border border-input-border bg-surface-1 px-2 text-xs font-medium text-muted-foreground hover:border-input-border-hover hover:bg-surface-3"
                  >
                    {t('skillPicker.cancel')}
                  </button>
                  <button
                    type="submit"
                    className="h-7 rounded-lg bg-primary px-2 text-xs font-medium text-white hover:bg-primary/90"
                    data-testid="skill-editor-add-file"
                  >
                    {t('skillPicker.addFile')}
                  </button>
                </div>
              </form>
            )}
            <button
              type="button"
              onClick={() => void selectPath(SKILL_DOCUMENT_PATH)}
              className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 ${selectedPath === SKILL_DOCUMENT_PATH ? 'bg-surface-2 text-foreground' : 'text-muted-foreground hover:bg-surface-2 hover:text-foreground'}`}
              data-testid="skill-file-SKILL.md"
            >
              <FileIcon className="h-4 w-4 text-primary" /> {SKILL_DOCUMENT_PATH}
            </button>
            {treeEntries.map((entry) =>
              entry.kind === 'folder' ? (
                <div
                  key={`folder:${entry.path}`}
                  className="flex items-center gap-1.5 py-1.5 pr-2 text-xs font-medium text-text-muted"
                  style={{ paddingLeft: `${10 + entry.depth * 14}px` }}
                  title={entry.path}
                  data-testid={`skill-folder-${entry.path}`}
                >
                  <ChevronRight className="h-3 w-3 shrink-0" /> <Folder className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate">{displayFileName(entry.path)}</span>
                </div>
              ) : (
                <button
                  key={`file:${entry.path}`}
                  type="button"
                  onClick={() => void selectPath(entry.path)}
                  className={`flex w-full items-center gap-2 rounded-lg py-1.5 pr-2 text-left text-xs font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 ${selectedPath === entry.path ? 'bg-surface-2 text-foreground' : 'text-muted-foreground hover:bg-surface-2 hover:text-foreground'}`}
                  style={{ paddingLeft: `${10 + entry.depth * 14}px` }}
                  aria-label={entry.path}
                  title={entry.path}
                  data-testid={`skill-file-${entry.path}`}
                >
                  <FileIcon className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">{displayFileName(entry.path)}</span>
                </button>
              ),
            )}
          </aside>

          <div className="relative min-w-0 overflow-hidden bg-background">
            {loadingFile ? (
              <div className="flex h-full items-center justify-center text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            ) : selectedIsText && previewMode === 'code' ? (
              <textarea
                value={selectedText}
                onChange={(event) => updateSelectedText(event.target.value)}
                spellCheck={false}
                className="h-full min-h-[360px] w-full resize-none bg-input-bg p-4 font-mono text-sm leading-relaxed text-foreground outline-none focus:ring-2 focus:ring-inset focus:ring-primary/20"
                aria-label={t('skillPicker.editFile', { path: selectedPath })}
                data-testid="skill-editor-code"
              />
            ) : selectedIsText ? (
              <div
                className="m-3 h-[calc(100%-24px)] overflow-y-auto rounded-xl border border-border bg-surface-0 shadow-lg"
                data-testid="skill-editor-markdown-preview"
              >
                <div
                  className={`sticky top-0 z-10 flex h-12 items-center gap-2 border-b border-border-subtle bg-surface-1 px-4 ${selectedPath !== SKILL_DOCUMENT_PATH ? 'pr-44' : ''}`}
                  data-testid="skill-editor-markdown-file-header"
                >
                  <FileIcon className="h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={1.5} aria-hidden="true" />
                  <span className="truncate font-mono text-sm font-medium text-muted-foreground">{selectedPath}</span>
                </div>
                <article className="mx-auto max-w-4xl px-6 py-7 sm:px-9 sm:py-8">
                  <ReactMarkdown components={skillMarkdownComponents}>
                    {selectedPath === SKILL_DOCUMENT_PATH ? documentBody : selectedText}
                  </ReactMarkdown>
                </article>
              </div>
            ) : (
              <div className="flex h-full min-h-[360px] flex-col items-center justify-center gap-2 text-muted-foreground">
                <FileIcon className="h-8 w-8" strokeWidth={1.5} />
                <p className="text-sm">{selectedPath}</p>
                <p className="text-xs font-medium text-text-muted">{t('skillPicker.binaryHint')}</p>
              </div>
            )}
            {selectedPath !== SKILL_DOCUMENT_PATH && (
              <div className="absolute right-3 top-3 z-10 flex items-center gap-1.5">
                {selectedIsReference && (
                  <button
                    type="button"
                    onClick={() => {
                      setRenameOpen((current) => !current)
                      setRenamePath(selectedPath)
                      setFileError(null)
                    }}
                    className="h-8 rounded-lg border border-input-border bg-surface-1 px-2.5 text-xs font-medium text-foreground hover:border-input-border-hover hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
                    data-testid="skill-editor-rename-file"
                  >
                    {t('skillPicker.rename')}
                  </button>
                )}
                <button
                  type="button"
                  onClick={removeSelected}
                  className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-input-border bg-surface-1 px-2.5 text-xs font-medium text-destructive hover:border-destructive/60 hover:bg-destructive/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-destructive/30"
                  data-testid="skill-editor-remove-file"
                >
                  <Trash2 className="h-3.5 w-3.5" /> {t('skillPicker.remove')}
                </button>
              </div>
            )}
            {renameOpen && selectedIsReference && (
              <form
                className="absolute right-3 top-12 z-20 w-72 space-y-2 rounded-lg border border-border bg-surface-1 p-2 shadow-xl"
                onSubmit={(event) => {
                  event.preventDefault()
                  renameSelected()
                }}
                data-testid="skill-editor-rename-form"
              >
                <label className="block text-xs font-medium text-muted-foreground" htmlFor="skill-editor-rename-path">
                  {t('skillPicker.filePath')}
                </label>
                <input
                  id="skill-editor-rename-path"
                  value={renamePath}
                  onChange={(event) => setRenamePath(event.target.value)}
                  className="h-8 w-full rounded-lg border border-input-border bg-input-bg px-2 text-xs font-medium text-foreground outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                  data-testid="skill-editor-rename-path"
                  autoFocus
                />
                <div className="flex justify-end gap-1.5">
                  <button
                    type="button"
                    onClick={() => {
                      setRenameOpen(false)
                      setRenamePath('')
                    }}
                    className="h-7 rounded-lg border border-input-border bg-surface-1 px-2 text-xs font-medium text-muted-foreground hover:border-input-border-hover hover:bg-surface-3"
                  >
                    {t('skillPicker.cancel')}
                  </button>
                  <button
                    type="submit"
                    className="h-7 rounded-lg bg-primary px-2 text-xs font-medium text-white hover:bg-primary/90"
                    data-testid="skill-editor-confirm-rename"
                  >
                    {t('skillPicker.rename')}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>

        <div className="space-y-1.5" data-testid="skill-editor-package-status">
          {limitsLoading && <p className="text-xs font-medium text-text-muted">{t('skillPicker.packageLimitsLoading')}</p>}
          {limitsError && (
            <div
              role="alert"
              className="flex items-center justify-between gap-3 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs font-medium text-destructive"
            >
              <span>
                {t('skillPicker.packageLimitsLoadFailed', {
                  reason: limitsError,
                })}
              </span>
              <button
                type="button"
                onClick={() => void loadLimits()}
                className="h-7 shrink-0 rounded-lg border border-destructive/50 bg-surface-1 px-2 text-xs font-medium hover:bg-destructive/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-destructive/30"
                data-testid="skill-editor-retry-limits"
              >
                {t('skillPicker.retry')}
              </button>
            </div>
          )}
          {limits && (
            <p className="text-xs font-medium text-text-muted" data-testid="skill-editor-package-usage">
              {t('skillPicker.packageUsage', {
                actualFiles: packageUsage.userFileCount,
                limitFiles: limits.max_user_file_count,
                actualBytes: packageUsage.userPayloadBytes,
                limitBytes: limits.max_user_payload_bytes,
              })}
            </p>
          )}
          {limitViolationMessage && (
            <p
              role="alert"
              className="rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
              data-testid="skill-editor-limit-error"
            >
              {limitViolationMessage}
            </p>
          )}
          {fileError && (
            <p
              role="alert"
              className="rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
              data-testid="skill-editor-file-error"
            >
              {fileError}
            </p>
          )}
          {documentError && (
            <p
              role="alert"
              className="rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
              data-testid="skill-editor-document-error"
            >
              {t('skillPicker.invalidYaml', { reason: documentError })}
            </p>
          )}
          {!documentError && validationLoading && (
            <p className="text-xs font-medium text-text-muted" data-testid="skill-editor-validation-loading">
              {t('skillPicker.validatingSkillDocument')}
            </p>
          )}
          {!documentError && validationError && (
            <div
              role="alert"
              className="flex items-center justify-between gap-3 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
              data-testid="skill-editor-validation-error"
            >
              <span>{validationError}</span>
              <button
                type="button"
                onClick={() => setValidationAttempt((current) => current + 1)}
                className="h-7 shrink-0 rounded-lg border border-destructive/50 bg-surface-1 px-2 text-xs font-medium hover:bg-destructive/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-destructive/30"
                data-testid="skill-editor-retry-validation"
              >
                {t('skillPicker.retry')}
              </button>
            </div>
          )}
        </div>
      </section>

      <section className="space-y-2" aria-labelledby="skill-cover-heading">
        <div>
          <h3 id="skill-cover-heading" className="text-lg font-medium text-foreground">
            {t('skillPicker.coverMedia')} <span className="font-normal text-text-muted">({t('skillPicker.optional')})</span>
          </h3>
          <p className="mt-0.5 text-xs font-medium text-text-muted">{t('skillPicker.coverHint')}</p>
        </div>
        <div className="flex items-start gap-2">
          <button
            type="button"
            onClick={() => coverInputRef.current?.click()}
            disabled={packageActionsDisabled}
            className="group flex h-32 w-52 items-center justify-center overflow-hidden rounded-xl border border-dashed border-input-border bg-input-bg text-muted-foreground hover:border-input-border-hover hover:bg-surface-2 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="skill-editor-cover-upload"
          >
            {pendingCoverUrl && pendingCoverKind === 'image' ? (
              <img src={pendingCoverUrl} alt={t('skillPicker.coverAlt', { name: fields.displayName })} className="h-full w-full object-cover" />
            ) : pendingCoverUrl && pendingCoverKind === 'video' ? (
              <video
                src={pendingCoverUrl}
                muted
                playsInline
                className="h-full w-full object-cover"
                aria-label={t('skillPicker.coverVideo', {
                  name: fields.displayName,
                })}
              />
            ) : detail.cover && coverPath ? (
              <SkillCoverMedia cover={detail.cover} title={fields.displayName} mode="preview" className="h-full w-full rounded-none border-0" />
            ) : (
              <span className="flex flex-col items-center gap-2 text-xs font-medium">
                <ImagePlus className="h-6 w-6" /> {t('skillPicker.uploadCover')}
              </span>
            )}
          </button>
          {coverPath && (
            <button
              type="button"
              onClick={removeCover}
              className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-input-border bg-surface-1 px-2.5 text-xs font-medium text-destructive hover:border-destructive/60 hover:bg-destructive/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-destructive/30"
              data-testid="skill-editor-remove-cover"
            >
              <Trash2 className="h-3.5 w-3.5" /> {t('skillPicker.remove')}
            </button>
          )}
        </div>
      </section>

      {error && (
        <p role="alert" className="rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </p>
      )}

      <div className="flex justify-end gap-2 border-t border-border-subtle pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="h-9 rounded-lg border border-input-border bg-surface-1 px-4 text-sm text-foreground hover:border-input-border-hover hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
        >
          {t('skillPicker.cancel')}
        </button>
        <button
          type="button"
          onClick={() => { trackSkillLibraryAction(analyticsProjectId, 'save_version', skill); void save() }}
          disabled={packageActionsDisabled || limitViolation !== null || validationLoading || validationError !== null || validatedContent !== content}
          className="inline-flex h-9 items-center gap-2 rounded-lg bg-primary px-4 text-sm font-medium text-white hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 disabled:opacity-50"
          data-testid="skill-editor-save"
        >
          {saving && <Loader2 className="h-4 w-4 animate-spin" />} {t('skillPicker.save')}
        </button>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".md,text/markdown"
        className="hidden"
        onChange={(event) => {
          const next = [...(event.target.files ?? [])]
          event.target.value = ''
          void addFiles(next)
        }}
        data-testid="skill-editor-file-input"
      />
      <input
        ref={folderInputRef}
        type="file"
        multiple
        accept=".md,text/markdown"
        className="hidden"
        onChange={(event) => {
          const next = [...(event.target.files ?? [])]
          event.target.value = ''
          void addFiles(next)
        }}
        data-testid="skill-editor-folder-input"
      />
      <input
        ref={coverInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,video/mp4,video/webm,video/quicktime"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0]
          event.target.value = ''
          if (file) replaceCover(file)
        }}
        data-testid="skill-editor-cover-input"
      />
    </div>
  )
}
